/**
 * "I need a camera" sign-in page - full-bleed kiosk screen reached when the
 * user picks the first tile on the Camera-checkout selection screen.
 *
 * Layout mirrors the pairing card (same `.ck-card` / `.ck-input` styles):
 *   - Back arrow (top-left, returns to /checkout/camera)
 *   - Walgreens brand
 *   - Exit / Help / English header actions
 *   - Card with title, device image (blinking.png), subtitle, two fields,
 *     Checkout button and Forgot PIN / Set up PIN links.
 *
 * NOTE: The real backend for this flow (assign a camera from a dock to the
 * signed-in employee) is TBD. For now the Checkout button validates that
 * both fields are filled and navigates to the existing success page so the
 * UX is end-to-end navigable.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Combobox from './Combobox';
import { fetchEmployees, autoPair } from './api';
import userService from '../../services/userService';

/** Resolve a public-folder asset URL respecting Vite's BASE_URL. */
const ICON_BASE =
  `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/icons/camera-checkout-page`;

export default function NeedCameraPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const checkoutId = params.get('checkoutId');
  const orgName  = params.get('org')  || '';
  const siteName = params.get('site') || '';
  const orgId    = params.get('orgId')  || '';
  const siteId   = params.get('siteId') || '';

  const buildQs = (extra = {}) => {
    const qp = new URLSearchParams();
    if (checkoutId) qp.set('checkoutId', checkoutId);
    if (orgName)    qp.set('org',    orgName);
    if (siteName)   qp.set('site',   siteName);
    if (orgId)      qp.set('orgId',  orgId);
    if (siteId)     qp.set('siteId', siteId);
    for (const [k, v] of Object.entries(extra)) if (v != null) qp.set(k, v);
    const s = qp.toString();
    return s ? `?${s}` : '';
  };

  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [userId, setUserId] = useState(null);
  const [comboText, setComboText] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch the employee list for the selected org/site - mirrors PairingPage.
  useEffect(() => {
    if (!orgId || !siteId) { setEmployees([]); return; }
    let cancelled = false;
    setLoadingEmployees(true);
    fetchEmployees(orgId, siteId)
      .then((list) => { if (!cancelled) setEmployees(list); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load employees'); })
      .finally(() => { if (!cancelled) setLoadingEmployees(false); });
    return () => { cancelled = true; };
  }, [orgId, siteId]);

  const employeeOptions = useMemo(
    () => employees.map((e) => ({ value: e.id, label: e.name, searchText: e.employeeId })),
    [employees],
  );

  const canSubmit = (!!userId || comboText.trim().length > 0) && /^[0-9]{4,8}$/.test(pin);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (!orgId || !siteId) {
        throw new Error('Missing organization or site context. Please relaunch from the Checkouts list.');
      }
      let emp = employees.find((x) => x.id === userId);
      if (!emp && comboText.trim()) {
        const text = comboText.trim().toLowerCase();
        emp = employees.find((e) =>
          e.name.toLowerCase() === text ||
          e.employeeId.toLowerCase() === text
        );
      }
      if (!emp) {
        setError('Please select a valid user from the list.');
        setSubmitting(false);
        return;
      }

      const result = await userService.verifyPin({
        orgId,
        siteId,
        employeeId: emp.id,
        pin,
      });
      if (!result?.valid) {
        setError(result?.reason || 'Invalid name or PIN. Please try again.');
        setSubmitting(false);
        return;
      }

      // PIN verified - ask the kiosk pairing service to assign an available
      // camera from the dock to this employee. Failures here are surfaced
      // inline so the user can retry without losing their typed PIN.
      try {
        await autoPair({ orgId, siteId, userId: emp.id });
      } catch (pairErr) {
        const msg = pairErr?.payload?.detail
          || pairErr?.message
          || 'Could not assign a camera, please try again.';
        setError(msg);
        setSubmitting(false);
        return;
      }

      const empIdForUrl = result.employeeId != null
        ? String(result.employeeId)
        : String(emp.employeeId || emp.id);
      navigate(`/checkout/pick-camera${buildQs({ name: emp.name, employeeId: empIdForUrl })}`);
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        err?.response?.data?.reason;
      setError(apiMsg || err?.message || 'Checkout failed, please try again.');
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(`/checkout/camera${buildQs()}`);
  };

  return (
    <div className="ck-page">
      <header className="ck-header nc-header">
        <div className="nc-header-left">
          <button type="button" className="nc-back-btn" title="Back" onClick={handleBack}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <div className="cc-brand">
            {orgName || 'Organization'}
            {siteName && <span className="cc-brand-site">/{siteName}</span>}
          </div>
        </div>
        <div className="cc-header-actions">
          <button
            type="button"
            className="cc-header-btn"
            title="Exit"
            onClick={() => navigate('/checkout')}
          >
            <img src={`${ICON_BASE}/signout.svg`} alt="" className="cc-header-icon-img" />
          </button>
          <button type="button" className="cc-header-btn" title="Help">
            <img src={`${ICON_BASE}/questionmark.svg`} alt="" className="cc-header-icon-img" />
          </button>
          <button type="button" className="cc-header-btn cc-header-lang">
            English <i className="fas fa-chevron-down"></i>
          </button>
        </div>
      </header>

      <main className="ck-main">
        <div className="ck-card nc-card">
          <h2 className="ck-card-title">Sign in to checkout a camera</h2>
          <p className="ck-card-subtitle">Sign in to assign a camera and start your shift</p>

          <div className="ck-device-box nc-device-box">
            <img
              src={`${ICON_BASE}/need-camera-guide.png`}
              alt="Camera docked and ready"
              className="ck-device-img nc-device-img"
            />
          </div>


          <form className="ck-form" onSubmit={handleSubmit} noValidate>
            <div className="ck-field">
              <label className="ck-label" htmlFor="nc-name">
                Name/Employee ID<span className="ck-required">*</span>
              </label>
              <Combobox
                id="nc-name"
                options={employeeOptions}
                value={userId}
                onChange={(v) => { setUserId(v); setError(null); }}
                onInputChange={setComboText}
                placeholder={
                  !orgId || !siteId ? 'Missing organization or site'
                  : loadingEmployees ? 'Loading employees...'
                  : 'Search your name or ID'
                }
                disabled={!orgId || !siteId}
                loading={loadingEmployees}
              />
            </div>

            <div className="ck-field">
              <label className="ck-label" htmlFor="nc-pin">
                PIN<span className="ck-required">*</span>
              </label>
              <input
                id="nc-pin"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={8}
                className="ck-input ck-pin"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>

            {error && <div className="ck-general-error">{error}</div>}

            <button type="submit" className="ck-submit" disabled={!canSubmit || submitting}>
              {submitting ? 'Checking out...' : 'Checkout'}
            </button>

            <div className="nc-links">
              <button type="button" className="nc-link" onClick={() => { /* TODO: forgot PIN flow */ }}>
                Forgot PIN?
              </button>
              <span className="nc-link-sep">|</span>
              <button type="button" className="nc-link" onClick={() => { /* TODO: set up PIN flow */ }}>
                Set up your PIN
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}


